import React from "react";
import { SxStyleProp } from "theme-ui";
import Box from "../Box/Box";
import Flex from "../Flex/Flex";

const TabContext = React.createContext<TabContextType>({} as TabContextType);
const useTabContext = () => React.useContext(TabContext);

interface TabContextType {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onChangeTab?: (index: number) => void;
}

interface Props {
  sx?: SxStyleProp;
  defaultIndex?: number;
  onChangeTab?: (index: number) => void;
}

export const Tabs: React.FunctionComponent<Props> = React.forwardRef(
  ({ sx, children, defaultIndex, onChangeTab, ...restProps }, ref) => {
    const [activeIndex, setActiveIndex] = React.useState(defaultIndex || 0);

    return (
      <TabContext.Provider value={{ activeIndex, setActiveIndex, onChangeTab }}>
        <Box
          ref={ref}
          sx={{
            ...(sx as any),
          }}
          {...restProps}
        >
          {children}
        </Box>
      </TabContext.Provider>
    );
  },
);

export const TabList: React.FunctionComponent<{
  sx?: SxStyleProp;
  children: React.ReactElement[];
}> = React.forwardRef(({ sx, children, ...restProps }, ref) => {
  const { activeIndex, setActiveIndex, onChangeTab } = useTabContext();

  const clones = React.Children.map(
    children,
    (child: React.ReactElement, index) => {
      const isActive = activeIndex === index;
      const handleClick = (event: React.HTMLAttributes<HTMLDivElement>) => {
        setActiveIndex(index);
        if (onChangeTab) {
          onChangeTab(index);
        }
        if (child.props.onClick) {
          child.props.onClick(event);
        }
      };

      return React.cloneElement(child, {
        isActive,
        onClick: handleClick,
        id: `tab-${index}`,
      });
    },
  );

  return (
    <Flex
      ref={ref}
      role="tablist"
      sx={{
        borderBottom: "1px solid",
        borderBottomColor: "primary",
        ...(sx as any),
      }}
      {...restProps}
    >
      {clones}
    </Flex>
  );
});

export const Tab: React.FunctionComponent<{
  sx?: SxStyleProp;
  isActive?: boolean;
}> = React.forwardRef(({ sx, isActive, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      sx={{
        cursor: "pointer",
        color: isActive ? "black" : "primary",
        textDecoration: isActive ? "underline" : undefined,
        fontWeight: "medium",
        my: 4,

        "&:not(:last-child)": {
          mr: 4,
        },
        ...(sx as any),
      }}
      {...restProps}
    />
  );
});

export const TabPanels: React.FunctionComponent<{
  sx?: SxStyleProp;
  children: React.ReactNode[];
}> = React.forwardRef(({ sx, children, ...restProps }, ref) => {
  const { activeIndex } = useTabContext();

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        ...(sx as any),
      }}
      {...restProps}
    >
      {children[activeIndex]}
    </Box>
  );
});

export const TabPanel: React.FunctionComponent<{
  sx?: SxStyleProp;
}> = React.forwardRef(({ sx, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        ...(sx as any),
      }}
      {...restProps}
    />
  );
});
